import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { isFirstDayOfMonth, getDaysInMonth, getDate, format } from 'date-fns'
import { FormikProps, withFormik } from 'formik'
import * as yup from 'yup'
import { get } from 'lodash-es'
import { Button, CurrencyField, FormField, Divider, InfoBox, Heading } from '../../../../components'
import styles from './PortfolioRentAndFeesForm.module.scss'
import RouterPromptModal from 'components/organisms/Modals/RouterPromptModal/RouterPromptModal'
import SvgInfo from 'views/components/atoms/Icons/Controls/Info'
import { formatCurrencyValue } from 'utils/currency'

type CurrencyValue = string | number

interface FormValues {
  keyDepositAmount: CurrencyValue
  serviceDepositAmount: CurrencyValue
  damageDepositAmount: CurrencyValue
  monthlyRentAmount: CurrencyValue
  leaseFee: {
    netAmount: CurrencyValue
    vatable: boolean
  }
  applicationFee: {
    netAmount: CurrencyValue
    vatable: boolean
  }
  administrationFee: {
    netAmount: CurrencyValue
    vatable: boolean
  }
  firstMonthRentAmount: CurrencyValue
}

interface IPortfolioRentAndFeesFormProps {
  initialValues: FormValues
  portfolioId: string
  redirect: (url: string) => void
  onSubmit: (values: FormValues) => void
  isDraftPortfolio: boolean
  isReadOnly: boolean
  leaseStartDate: string
}

const validationSchema = yup.object().shape({
  monthlyRentAmount: yup.string().required('required.'),
})

const PortfolioRentAndFeesForm: React.FC<IPortfolioRentAndFeesFormProps & FormikProps<FormValues>> = ({
  portfolioId,
  redirect,
  handleSubmit,
  submitForm,
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  isDraftPortfolio,
  isReadOnly,
  leaseStartDate,
  dirty,
  isValid,
  isSubmitting,
}) => {
  const getFieldErrors = (name: string) => get(touched, name, false) && get(errors, name, '')

  // Next is gated on the required fields being filled in, not on the form being dirty —
  // a draft reloaded with a rental amount already captured must still be able to continue.
  const hasRequiredFields = Boolean(values.monthlyRentAmount)

  // Pro-rata rent only applies when the lease starts partway through a month.
  const showProRataRent = Boolean(leaseStartDate) && !isFirstDayOfMonth(new Date(leaseStartDate))

  // Pro-rata rent is auto-calculated from the rental amount and remaining days in the
  // start month, but a user can override it — once they do, we stop overwriting it.
  const [isProRataEdited, setIsProRataEdited] = useState(Boolean(values.firstMonthRentAmount))

  const proRata = useMemo(() => {
    if (!showProRataRent) return null
    const start = new Date(leaseStartDate)
    const daysInMonth = getDaysInMonth(start)
    const remainingDays = daysInMonth - getDate(start) + 1
    const monthlyRentCents = Number(values.monthlyRentAmount) || 0
    return {
      amount: Math.round((monthlyRentCents * remainingDays) / daysInMonth),
      remainingDays,
      startLabel: format(start, 'd MMMM yyyy'),
      monthName: format(start, 'MMMM'),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProRataRent, leaseStartDate, values.monthlyRentAmount])

  useEffect(() => {
    if (proRata && !isProRataEdited) {
      setFieldValue('firstMonthRentAmount', proRata.amount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proRata?.amount, isProRataEdited])

  return (
    <div className={styles.root}>
      <RouterPromptModal
        when={dirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onSave={submitForm}
        onDiscard={() => true}
      />
      <form className={styles.form} onSubmit={handleSubmit}>
        <fieldset disabled={isReadOnly}>
          <div className={styles.body}>
            {!isDraftPortfolio && (
              <div className="rbn--row">
                <div className="rbn--col-md-8">
                  <InfoBox type="info" className={styles['info-box']}>
                    <header>
                      <SvgInfo />
                      <p>If you intend to renew this lease, use the Renewal step instead.</p>
                    </header>
                  </InfoBox>
                </div>
              </div>
            )}
            {isDraftPortfolio && (
              <div className={styles['required-fields-info']}>
                Fields marked with <span>*</span> are required.
              </div>
            )}
            <div className="rbn--row">
              <div className="rbn--col-md-4">
                <FormField>
                  <CurrencyField
                    name="monthlyRentAmount"
                    label="Rental amount"
                    value={values.monthlyRentAmount}
                    error={getFieldErrors('monthlyRentAmount')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('monthlyRentAmount', e.target.value)
                      // The rental amount is the source of truth for pro-rata rent, so
                      // changing it re-drives the auto-calculation (dropping any override).
                      setIsProRataEdited(false)
                    }}
                    required={true}
                  />
                </FormField>
              </div>
            </div>
            {showProRataRent && isDraftPortfolio ? (
              <div className="rbn--row">
                <div className="rbn--col-md-8">
                  <InfoBox type="locked" className={styles['pro-rata-info-box']}>
                    <FormField>
                      <CurrencyField
                        name="firstMonthRentAmount"
                        label="Pro-rata rental amount"
                        labelHint="Calculated as the rental amount divided by the number of days in the month, multiplied by the remaining days from your start date. You can adjust this if needed."
                        placeholder="0 000.00"
                        value={values.firstMonthRentAmount}
                        error={getFieldErrors('firstMonthRentAmount')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('firstMonthRentAmount', e.target.value)
                          setIsProRataEdited(true)
                        }}
                      />
                    </FormField>
                    {proRata && (
                      <p>
                        Calculated based on start date of {proRata.startLabel} — {proRata.remainingDays} remaining{' '}
                        {proRata.remainingDays === 1 ? 'day' : 'days'} in {proRata.monthName}.
                      </p>
                    )}
                  </InfoBox>
                </div>
              </div>
            ) : values.firstMonthRentAmount ? (
              <div className="rbn--row">
                <div className="rbn--col-md-4">
                  <InfoBox type="locked" className={styles['info-box']}>
                    <div className={styles['read-only-field']}>
                      <span>Pro-rata rent</span>
                      <p>R {values.firstMonthRentAmount ? formatCurrencyValue(values.firstMonthRentAmount) : '0.00'}</p>
                    </div>
                  </InfoBox>
                </div>
              </div>
            ) : null}

            <Divider>
              <Heading as="h3">Deposits</Heading>
            </Divider>

            <div className="rbn--row">
              <div className="rbn--col-md-4">
                <FormField>
                  <CurrencyField
                    name="damageDepositAmount"
                    label="Damage deposit"
                    error={getFieldErrors('damageDepositAmount')}
                    value={values.damageDepositAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('damageDepositAmount', e.target.value)
                    }
                  />
                </FormField>
              </div>
              <div className="rbn--col-md-4">
                <FormField>
                  <CurrencyField
                    name="serviceDepositAmount"
                    label="Service deposit"
                    error={getFieldErrors('serviceDepositAmount')}
                    value={values.serviceDepositAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('serviceDepositAmount', e.target.value)
                    }
                  />
                </FormField>
              </div>
              <div className="rbn--col-md-4">
                <FormField>
                  <CurrencyField
                    name="keyDepositAmount"
                    label="Key deposit"
                    error={getFieldErrors('keyDepositAmount')}
                    value={values.keyDepositAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('keyDepositAmount', e.target.value)
                    }
                  />
                </FormField>
              </div>
            </div>

            <Divider>
              <Heading as="h3">Fees</Heading>
            </Divider>

            {isDraftPortfolio ? (
              <>
                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <FormField>
                      <CurrencyField
                        includeVatControl
                        vatApplied={values.applicationFee.vatable}
                        name="applicationFee.netAmount"
                        label={`Application fee ${values.applicationFee.vatable ? '(incl. VAT)' : ''}`}
                        error={getFieldErrors('applicationFee.netAmount')}
                        value={values.applicationFee.netAmount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('applicationFee.netAmount', e.target.value)
                        }
                        onToggleChange={(vatApplied: boolean) => {
                          setFieldValue('applicationFee.vatable', vatApplied)
                        }}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-md-4">
                    <FormField>
                      <CurrencyField
                        includeVatControl
                        vatApplied={values.administrationFee.vatable}
                        name="administrationFee.netAmount"
                        label={`Administration fee ${values.administrationFee.vatable ? '(incl. VAT)' : ''}`}
                        error={getFieldErrors('administrationFee.netAmount')}
                        value={values.administrationFee.netAmount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('administrationFee.netAmount', e.target.value)
                        }
                        onToggleChange={(vatApplied: boolean) => {
                          setFieldValue('administrationFee.vatable', vatApplied)
                        }}
                      />
                    </FormField>
                  </div>
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <FormField>
                      <CurrencyField
                        includeVatControl
                        vatApplied={values.leaseFee.vatable}
                        name="leaseFee.netAmount"
                        label={`Lease fee ${values.leaseFee.vatable ? '(incl. VAT)' : ''}`}
                        value={values.leaseFee.netAmount}
                        error={getFieldErrors('leaseFee.netAmount')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('leaseFee.netAmount', e.target.value)
                        }
                        onToggleChange={(vatApplied: boolean) => {
                          setFieldValue('leaseFee.vatable', vatApplied)
                        }}
                      />
                    </FormField>
                  </div>
                </div>
              </>
            ) : (
              <InfoBox type="locked" className={styles['info-box']}>
                <div className="rbn--row">
                  <div className="rbn--col-md-4">
                    <div className={styles['read-only-field']}>
                      <span>Application fee</span>
                      <p>
                        R{' '}
                        {values.applicationFee.netAmount
                          ? formatCurrencyValue(values.applicationFee.netAmount)
                          : '0.00'}
                      </p>
                    </div>
                  </div>
                  <div className="rbn--col-md-4">
                    <div className={styles['read-only-field']}>
                      <span>Administration fee</span>
                      <p>
                        R{' '}
                        {values.administrationFee.netAmount
                          ? formatCurrencyValue(values.administrationFee.netAmount)
                          : '0.00'}
                      </p>
                    </div>
                  </div>
                  <div className="rbn--col-md-4">
                    <div className={styles['read-only-field']}>
                      <span>Lease fee</span>
                      <p>R {values.leaseFee.netAmount ? formatCurrencyValue(values.leaseFee.netAmount) : '0.00'}</p>
                    </div>
                  </div>
                </div>
              </InfoBox>
            )}
          </div>

          <div className={isDraftPortfolio ? styles.footer : styles['footer--active']}>
            {isDraftPortfolio ? (
              <>
                <Button onClick={() => redirect(`/leases/${portfolioId}/edit/term`)}>Back</Button>
                <Button disabled={!hasRequiredFields || Object.keys(errors).length > 0} type="submit">
                  Next
                </Button>
              </>
            ) : (
              <Button disabled={!dirty || Object.keys(errors).length > 0} type="submit">
                Save
              </Button>
            )}
          </div>
        </fieldset>
      </form>
    </div>
  )
}

export default withFormik<IPortfolioRentAndFeesFormProps, FormValues>({
  validationSchema,
  validateOnBlur: true,
  enableReinitialize: true,
  mapPropsToValues: props => ({ ...props.initialValues }),
  handleSubmit: (values, { props }) => {
    // Pro-rata rent is only relevant for mid-month starts; drop any stale value otherwise.
    const startsMidMonth = Boolean(props.leaseStartDate) && !isFirstDayOfMonth(new Date(props.leaseStartDate))
    props.onSubmit({ ...values, firstMonthRentAmount: startsMidMonth ? values.firstMonthRentAmount : '' })
  },
})(PortfolioRentAndFeesForm)
